import { useFieldArray } from 'react-hook-form';
import PropTypes from 'prop-types';
import { Alert, Box, ListSubheader, Button } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import ScheduleListItem from './ScheduleListItem';
import dayjs from 'dayjs';
import { useEffect, useState, useCallback } from 'react';
import debounce from 'lodash/debounce';

const ScheduleDate = ({ control, nestedIndex, fieldName, subfieldName, date, kansanopisto, setHours, isReassigning }) => {
    const { getValues, trigger, watch } = useFormContext();

    const { fields, remove, append, replace } = useFieldArray({
        control,
        name: `${fieldName}[${nestedIndex}].${subfieldName}`,
    });

    const currentSchedule = watch(fieldName);
    const [renderKey, setRenderKey] = useState(0);

    // Validate tilaisuus for rendering
    const isValidTilaisuus = (tilaisuus) => {
        const isValid = tilaisuus &&
            typeof tilaisuus.nimi === 'string' && tilaisuus.nimi.trim().length > 0 &&
            tilaisuus.aloitus && dayjs(tilaisuus.aloitus).isValid();
        if (!isValid) {
            console.warn('Invalid tilaisuus in ScheduleDate:', { tilaisuus });
        }
        return isValid;
    };

    // Debounced syncFields
    const syncFields = useCallback(
        debounce(() => {
            const scheduleDay = currentSchedule?.[nestedIndex];
            const scheduleTilaisuudet = scheduleDay?.tilaisuudet || [];
            // Only sync if fields are missing tilaisuudet
            if (fields.length < scheduleTilaisuudet.length) {
                replace(scheduleTilaisuudet); // Allow invalid tilaisuudet
                trigger(`${fieldName}[${nestedIndex}].${subfieldName}`);
                setRenderKey(prev => prev + 1);
            }
        }, 500),
        [currentSchedule, fields.length, replace, trigger, fieldName, nestedIndex, subfieldName]
    );

    // Run sync and validation
    useEffect(() => {
        syncFields();
        trigger(`${fieldName}[${nestedIndex}].${subfieldName}`);
        return () => {
            syncFields.cancel();
        };
    }, [currentSchedule, fields.length, trigger, fieldName, nestedIndex, subfieldName, date]);

    const handleTilaisuusRemove = (nestedIndex, subFieldIndex) => {
        const fieldValues = getValues();
        const tilaisuudetArray = fieldValues[fieldName]?.[nestedIndex]?.tilaisuudet || [];
        const removedTilaisuus = tilaisuudetArray[subFieldIndex];
        const removedHours =
            removedTilaisuus?.kansanopistotunnit && removedTilaisuus?.kansanopistotunnit[0]?.kansanopistotunnit || 0;


        setHours((prevTotal) => prevTotal - removedHours);
        remove(subFieldIndex);
        trigger(`${fieldName}[${nestedIndex}].${subfieldName}`);
        setRenderKey(prev => prev + 1);
    };

    const handleAddTilaisuus = () => {
        const newTilaisuus = {
            id: crypto.randomUUID(),
            nimi: '',
            aloitus: null,
            lopetus: null,
            kuvaus: '',
            henkilot: [],
            kansanopistotunnit: kansanopisto ? [{ kansanopistotunnit: 0 }] : [],
        };
        try {
            append(newTilaisuus);
            setRenderKey(prev => prev + 1);
        } catch (error) {
            console.error('Failed to append tilaisuus:', { error, newTilaisuus });
        }
    };

    const subHeaderStyle = {
        textAlign: 'center',
        fontFamily: '"Bree Serif", sans-serif',
        fontSize: 18,
        color: 'black',
    };

    let content;

    if (!fields.length) {
        content = (
            <Alert component="li" severity="info" sx={{ borderRadius: 3 }}>
                Tälle päivälle ei vielä ole ohjelmaa
            </Alert>
        );
    } else {
        content = fields.map((field, index) => (
            <ScheduleListItem
                key={`${field.id}-${renderKey}`}
                kansanopisto={kansanopisto}
                fieldPath={`${fieldName}[${nestedIndex}].${subfieldName}[${index}]`}
                setHours={setHours}
                handleRemove={() => handleTilaisuusRemove(nestedIndex, index)}
                isReassigning={isReassigning}
            />
        ));
    }

    return (
        <>
            <ListSubheader variant="overline" disableSticky disableGutters sx={subHeaderStyle}>
                {date} (Päivä {nestedIndex + 1})
            </ListSubheader>
            {content}
            <Box display="flex" justifyContent="flex-end" p={0}>
                <Button
                    size="small"
                    onClick={handleAddTilaisuus}
                    variant="contained"
                    sx={{ backgroundColor: 'taphaBlue.main', whiteSpace: 'nowrap', px: 3, mt: 1 }}
                >
                    Lisää tilaisuus
                </Button>
            </Box>
        </>
    );
};

ScheduleDate.propTypes = {
    control: PropTypes.object,
    nestedIndex: PropTypes.number,
    fieldName: PropTypes.string,
    subfieldName: PropTypes.string,
    date: PropTypes.string,
    kansanopisto: PropTypes.any,
    setHours: PropTypes.func,
    isReassigning: PropTypes.bool,
};

export default ScheduleDate;